import { useTranslation } from 'react-i18next';
import type { CategoryScore } from '../completeness.api';

interface CategoryTabsProps {
  categories: CategoryScore[];
  activeCategory: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  incompleteCounts?: Record<string, number>;
}

export function CategoryTabs({
  categories,
  activeCategory,
  onCategoryChange,
  incompleteCounts = {},
}: CategoryTabsProps) {
  const { t } = useTranslation();

  return (
    <div data-testid="category-tabs" className="flex flex-wrap gap-2" role="tablist" aria-label={t('products.filterByCategory')}>
      {/* "Alle" tab */}
      <button
        type="button"
        role="tab"
        aria-selected={activeCategory === null}
        data-testid="category-tab-all"
        onClick={() => onCategoryChange(null)}
        className="rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-150"
        style={
          activeCategory === null
            ? {
                backgroundColor: 'var(--primary)',
                color: '#FFFFFF',
              }
            : {
                backgroundColor: 'var(--background-muted)',
                color: 'var(--foreground-muted)',
                border: '1px solid var(--border)',
              }
        }
      >
        {t('products.allCategories')}
      </button>

      {categories.map((cat) => {
        const isActive = activeCategory === cat.categoryId;
        const count = incompleteCounts[cat.categoryId] ?? 0;

        return (
          <button
            type="button"
            role="tab"
            aria-selected={isActive}
            key={cat.categoryId}
            data-testid={`category-tab-${cat.categoryId}`}
            onClick={() => onCategoryChange(cat.categoryId)}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-150"
            style={
              isActive
                ? {
                    backgroundColor: 'var(--primary)',
                    color: '#FFFFFF',
                  }
                : {
                    backgroundColor: 'var(--background-muted)',
                    color: 'var(--foreground-muted)',
                    border: '1px solid var(--border)',
                  }
            }
          >
            {cat.categoryName}
            {count > 0 && (
              <span
                className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[0.625rem] font-semibold"
                style={
                  isActive
                    ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }
                    : { backgroundColor: 'var(--warning-light)', color: 'var(--warning)' }
                }
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
